<template>
  <div class="viq-exec-summary">
    <div class="viq-exec-summary__badge">
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M3 8l4 4 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
      Report Complete
    </div>

    <div class="viq-exec-summary__card">
      <h3 class="viq-exec-summary__title">Executive Summary</h3>
      <div class="viq-exec-summary__content" v-html="renderedSummary" />
    </div>

    <div class="viq-exec-summary__sections-preview">
      <h4 class="viq-exec-summary__sections-title">Report Sections</h4>
      <div
        v-for="(section, i) in sections"
        :key="i"
        class="viq-exec-summary__section-item"
      >
        <span class="viq-exec-summary__section-num">{{ i + 1 }}</span>
        <span class="viq-exec-summary__section-name">{{ section.title }}</span>
      </div>
    </div>

    <div class="viq-exec-summary__actions">
      <el-button type="primary" @click="exportPdf">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style="margin-right: 6px; vertical-align: middle;">
          <path d="M12 9v3H2V9M7 1v8M4 5l3 3 3-3" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        Export as PDF
      </el-button>
      <el-button v-if="$config.features && $config.features.viqShare" @click="share">
        Save &amp; Share
      </el-button>
      <el-button @click="newReport">
        New Report
      </el-button>
    </div>
  </div>
</template>

<script>
import DOMPurify from "dompurify";

export default {
  name: "ViqReportExecSummary",
  props: {
    summary: {
      type: String,
      default: "",
    },
    sections: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    renderedSummary() {
      if (!this.summary) return "<p>No executive summary available.</p>";
      let html = this.summary;
      html = html.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
      html = html.replace(/\*(.+?)\*/g, "<em>$1</em>");
      html = html.replace(/^[*-] (.+)$/gm, "<li>$1</li>");
      html = html.replace(/((?:<li>.*<\/li>\n?)+)/g, "<ul>$1</ul>");
      html = html.replace(/\n\n/g, "</p><p>");
      html = "<p>" + html + "</p>";
      html = html.replace(/<p><\/p>/g, "");
      html = html.replace(/\n/g, "<br>");
      return DOMPurify.sanitize(html);
    },
  },
  methods: {
    exportPdf() {
      // Will be handled by ViqPdfExport
      this.$emit("export-pdf");
    },
    share() {
      this.$store.dispatch("Viq/openShareDialog", {
        type: "report",
        sections: this.sections,
        summary: this.summary,
      });
    },
    newReport() {
      this.$store.dispatch("Viq/newConversation");
      this.$store.commit("Viq/SET_MODE", "report");
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/viq.scss";

.viq-exec-summary {
  padding: $viq-space-lg 0;

  &__badge {
    display: inline-flex;
    align-items: center;
    gap: $viq-space-sm;
    padding: $viq-space-sm $viq-space-md;
    background: rgba(16, 185, 129, 0.08);
    color: $viq-success;
    border-radius: $viq-radius-full;
    font-size: $viq-font-sm;
    font-weight: 700;
    margin-bottom: $viq-space-lg;
  }

  &__card {
    padding: $viq-space-xl;
    background: $viq-bg-primary;
    border: 1px solid $viq-border;
    border-radius: $viq-radius-lg;
    margin-bottom: $viq-space-lg;
  }

  &__title {
    font-size: $viq-font-lg;
    font-weight: 700;
    color: $viq-text-primary;
    margin: 0 0 $viq-space-md;
  }

  &__content {
    font-size: $viq-font-base;
    line-height: 1.7;
    color: $viq-text-primary;

    ::v-deep {
      p { margin: 0 0 $viq-space-sm; }
      ul { padding-left: $viq-space-lg; margin: $viq-space-sm 0; }
      li { margin-bottom: $viq-space-xs; }
      strong { font-weight: 700; }
    }
  }

  &__sections-preview {
    margin-bottom: $viq-space-lg;
  }

  &__sections-title {
    font-size: $viq-font-sm;
    font-weight: 700;
    color: $viq-text-secondary;
    margin: 0 0 $viq-space-md;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  &__section-item {
    display: flex;
    align-items: center;
    gap: $viq-space-sm;
    padding: $viq-space-sm 0;
    border-bottom: 1px solid $viq-border;
  }

  &__section-num {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: $viq-bg-secondary;
    border-radius: 50%;
    font-size: $viq-font-xs;
    font-weight: 700;
    color: $viq-text-secondary;
  }

  &__section-name {
    font-size: $viq-font-base;
    color: $viq-text-primary;
  }

  &__actions {
    display: flex;
    gap: $viq-space-sm;
    flex-wrap: wrap;
  }
}
</style>
